import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
 
import { cn } from "../../../@/lib/utils"
import classNames from "classnames"
 
const buttonVariants = cva(
  // Note: When adding a new button variant, remember to add hover and active effects that suit that variant's style
  classNames(
    "cursor-pointer",
    "flex inline-flex items-center justify-center",
    "whitespace-nowrap rounded",
    "text-sm font-medium",
    'active:ring-2',
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
    "disabled:pointer-events-none disabled:opacity-50 disabled:ring-0", // When button is disabled ring should not be visible on clicking
    "transition-all duration-150",
  ),
  {
    variants: {
      variant: {
        default:
          "bg-green-700 h-full p-2.5 text-white hover:bg-green-900 active:ring-usfGreen",
        destructive:
          "bg-red-700 text-white shadow-sm hover:bg-red-800 active:ring-red-600",
        ghost: "hover:bg-accent hover:text-accent-foreground active:ring-0",
        outline: "bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 active:ring-gray-200"
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-10 px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)
 
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  abbr?: string; // abbreviation tooltip text for button when hovered on
}
 
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, abbr, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <abbr className="no-underline border-0" title={abbr ?? ""}> {/* The className for abbr is so that there are no faint dotted underline on the button texts */}
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        />
      </abbr>
    )
  }
)
Button.displayName = "Button"
 
export { Button, buttonVariants }
